import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/** Desktop layout kicks in from this width (matches the CSS breakpoints). */
export const DESKTOP_MIN = 900;
/** Wide desktop: the menu shows its category rail and 3-column item grid. */
export const WIDE_MIN = 1180;

/**
 * Viewport breakpoints shared by the shell and the screens, so the desktop
 * layout (top nav + footer + multi-column pages) switches in one place.
 */
@Injectable({ providedIn: 'root' })
export class ViewportService {
  private readonly _width = new BehaviorSubject<number>(window.innerWidth);
  readonly width$ = this._width.asObservable();
  readonly isDesktop$ = new BehaviorSubject<boolean>(window.innerWidth >= DESKTOP_MIN);

  constructor(zone: NgZone) {
    zone.runOutsideAngular(() => {
      window.addEventListener('resize', () => {
        const w = window.innerWidth;
        const desk = w >= DESKTOP_MIN;
        if (desk !== this.isDesktop$.value) zone.run(() => this.isDesktop$.next(desk));
        this._width.next(w);
      }, { passive: true });
    });
  }

  get width(): number { return this._width.value; }
  get isDesktop(): boolean { return this.isDesktop$.value; }
  get isWide(): boolean { return this.width >= WIDE_MIN; }
}

/**
 * Nearest scrollable ancestor (inclusive). On phones each screen scrolls its own
 * body; on desktop the shell's page container scrolls, so screens that jump to /
 * track sections must resolve the real scroller at runtime.
 */
export function scrollParentOf(el: HTMLElement | null): HTMLElement {
  let n: HTMLElement | null = el;
  while (n) {
    const oy = getComputedStyle(n).overflowY;
    if (oy === 'auto' || oy === 'scroll') return n;
    n = n.parentElement;
  }
  return (document.scrollingElement || document.documentElement) as HTMLElement;
}
